import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { ClientPortalBadge } from "../clientPortalUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Active", "Inactive"];
const EMPTY_FORM = {
  name: "",
  primaryContactName: "",
  email: "",
  phone: "",
  linkedProjectId: "",
  linkedCrmDealId: "",
  status: "Active",
};

export default function ClientAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [projectsRes, dealsRes] = await Promise.all([
        apiFetch("/api/projects"),
        apiFetch("/api/crm/deals"),
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (dealsRes.ok) setDeals(await dealsRes.json());
    } catch {
      setProjects([]);
      setDeals([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (status !== "All") params.set("status", status);
      const res = await apiFetch(`/api/client-portal/accounts?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load client accounts.");
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(accounts.length / PAGE_SIZE));
  const pagedAccounts = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return accounts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [accounts, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function selectDeal(dealId) {
    const deal = deals.find((item) => item.id === dealId);
    setForm((current) => ({
      ...current,
      linkedCrmDealId: dealId,
      name: deal?.accountName || current.name,
      primaryContactName: deal?.contactName || current.primaryContactName,
      email: deal?.contactEmail || current.email,
      phone: deal?.contactPhone || current.phone,
    }));
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEdit(account) {
    setEditing(account);
    setForm({
      name: account.name || "",
      primaryContactName: account.primaryContactName || "",
      email: account.email || "",
      phone: account.phone || "",
      linkedProjectId: account.linkedProjectId || "",
      linkedCrmDealId: account.linkedCrmDealId || "",
      status: account.status || "Active",
    });
    setModalOpen(true);
  }

  async function saveAccount(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        linkedProjectId: form.linkedProjectId || null,
        linkedCrmDealId: form.linkedCrmDealId || null,
      };
      const res = await apiFetch(
        editing ? `/api/client-portal/accounts/${editing.id}` : "/api/client-portal/accounts",
        {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not save client account.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Clients"
        subtitle={`${accounts.length} client account${accounts.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>Add Client</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search client, contact, email, phone"
            aria-label="Search client accounts"
          />
        </div>
        <div className="crm-stage-filter" aria-label="Client status">
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>
              {item}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : accounts.length === 0 ? (
          <EmptyState
            title={search || status !== "All" ? "No clients match your filters" : "No client accounts yet"}
            subtitle={search || status !== "All" ? "Clear the filters and try again." : "Add clients to expose projects, approvals, documents, and invoices in one workspace."}
            action={!search && status === "All" ? <button className="primary" onClick={openCreate}>Add Client</button> : null}
          />
        ) : (
          <>
            <div className="client-portal-table-wrap">
              <table className="client-portal-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Contact</th>
                    <th>Linked Work</th>
                    <th>Requests</th>
                    <th>Approvals</th>
                    <th>Documents</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <div className="client-portal-person-cell">
                          <div className="avatar">{account.name?.[0]?.toUpperCase() || "C"}</div>
                          <div>
                            <div className="client-portal-row-title">{account.name}</div>
                            <div className="client-portal-row-subtitle">Added {formatDate(account.createdAt)}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>{account.primaryContactName || "-"}</div>
                        <div className="client-portal-row-subtitle">{account.email || account.phone || ""}</div>
                      </td>
                      <td>
                        <div>{account.linkedProjectName || account.linkedCrmDealName || "-"}</div>
                        <div className="client-portal-row-subtitle">{account.linkedProjectName && account.linkedCrmDealName ? account.linkedCrmDealName : ""}</div>
                      </td>
                      <td>{account.openRequests}</td>
                      <td>{account.pendingApprovals}</td>
                      <td>{account.sharedDocuments}</td>
                      <td><ClientPortalBadge status={account.status} /></td>
                      <td>
                        <button className="btn-ghost" onClick={() => openEdit(account)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={accounts.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={saveAccount} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? "Edit Client" : "Add Client"}</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                CRM Deal
                <select value={form.linkedCrmDealId} onChange={(event) => selectDeal(event.target.value)}>
                  <option value="">No CRM deal</option>
                  {deals.map((deal) => <option key={deal.id} value={deal.id}>{deal.accountName}</option>)}
                </select>
              </label>
              <label>
                Linked Project
                <select value={form.linkedProjectId} onChange={(event) => updateField("linkedProjectId", event.target.value)}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Client Name
                <input value={form.name} onChange={(event) => updateField("name", event.target.value)} required />
              </label>
              <label>
                Primary Contact
                <input value={form.primaryContactName} onChange={(event) => updateField("primaryContactName", event.target.value)} />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} />
              </label>
              <label>
                Phone
                <input value={form.phone} onChange={(event) => updateField("phone", event.target.value)} />
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  {STATUSES.filter((item) => item !== "All").map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Save Client"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
